/**
 * Badge components for displaying Lambda execution metrics
 */

import { Badge } from '@/components/ui/badge';
import type { RequestMetrics } from './types';
import { formatMetricValue } from './formatters';

interface MetricBadgeProps {
  label: string;
  value: number | undefined;
  unit: string;
  variant?: 'outline' | 'secondary' | 'default';
}

/**
 * Single metric badge with label, value, and unit
 */
export function MetricBadge({
  label,
  value,
  unit,
  variant = 'outline',
}: MetricBadgeProps) {
  if (value === undefined) return null;

  return (
    <Badge variant={variant} className="font-mono text-xs">
      {label}: {formatMetricValue(value)}
      {unit}
    </Badge>
  );
}

interface RequestMetricsBadgesProps {
  metrics: RequestMetrics;
  isColdStart: boolean;
}

/**
 * Group of badges showing Lambda execution metrics
 *
 * Displays:
 * - Duration (ms)
 * - Max Memory Used (MB)
 * - Cold Start indicator with init duration (if applicable)
 */
export function RequestMetricsBadges({
  metrics,
  isColdStart,
}: RequestMetricsBadgesProps) {
  return (
    <div className="flex flex-wrap gap-1">
      <MetricBadge label="Duration" value={metrics.duration} unit="ms" />
      <MetricBadge
        label="Memory"
        value={metrics.maxMemoryUsed}
        unit="MB"
        variant="secondary"
      />
      {isColdStart && metrics.initDuration && (
        <Badge className="bg-blue-500/15 text-blue-600 border-blue-500/30 dark:text-blue-400 font-mono text-xs">
          Cold Start: {Math.round(metrics.initDuration)}ms
        </Badge>
      )}
    </div>
  );
}
